import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Typography,
  Popconfirm,
  Tag,
  Row,
  Col,
  Statistic,
  DatePicker,
  Tabs,
  Avatar,
  Switch,
  Checkbox,
  Radio,
  Divider,
  Alert
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  MailOutlined,
  UserAddOutlined,
  TeamOutlined,
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UploadOutlined,
  RobotOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import ReportSectionManager from './ReportSectionManager';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// 定义固定的新闻源列表
const NEWS_SOURCES = [
  { name: 'Bloomberg', label: 'Bloomberg' },
  { name: 'Wall Street Journal', label: 'Wall Street Journal' },
  { name: 'Financial Times', label: 'Financial Times' },
  { name: 'The Information', label: 'The Information' },
  { name: 'Wall Street CN', label: '华尔街见闻 (Wall Street CN)' },
  { name: 'WallStreet资讯', label: 'WallStreet资讯' },
  { name: 'ETNews', label: 'ETNews (韩国电子报)' },
  { name: 'The Elec', label: 'The Elec (韩国半导体)' },
  { name: 'MK', label: 'MK (韩国每日经济)' },
  { name: 'EE Times Japan', label: 'EE Times Japan (日本电子)' },
  { name: '經濟日報', label: '經濟日報 (台湾经济日报)' }
];

// 可用的 AI 模型列表
const AI_MODELS = [
  { 
    id: 'google/gemini-3-pro-preview', 
    name: 'Gemini 3 Pro Preview', 
    provider: 'Google',
    description: '高级推理，综合能力强'
  },
  { 
    id: 'openai/gpt-5.2', 
    name: 'GPT-5.2', 
    provider: 'OpenAI',
    description: '高性能推理，性价比优选'
  },
  { 
    id: 'openai/gpt-5.2-pro', 
    name: 'GPT-5.2 Pro', 
    provider: 'OpenAI',
    description: '高性能推理，企业级首选'
  },
  { 
    id: 'openai/gpt-5.4', 
    name: 'GPT-5.4', 
    provider: 'OpenAI',
    description: '高性能推理，性价比优选'
  },
  { 
    id: 'openai/gpt-5.4-pro', 
    name: 'GPT-5.4 Pro', 
    provider: 'OpenAI',
    description: '最新旗舰推理模型'
  },
  { 
    id: 'anthropic/claude-opus-4.5', 
    name: 'Claude Opus 4.5', 
    provider: 'Anthropic',
    description: '长文本理解，多轮对话'
  },
  { 
    id: 'z-ai/glm-5.2', 
    name: 'GLM-5.2', 
    provider: 'Z.ai',
    description: '超长上下文(1M)，推理与编码强'
  },
  { 
    id: 'moonshotai/kimi-k3', 
    name: 'Kimi K3', 
    provider: 'Moonshot AI',
    description: '开源旗舰推理模型，1M上下文'
  },
  { 
    id: 'openai/gpt-5.6-sol', 
    name: 'GPT-5.6 Sol', 
    provider: 'OpenAI',
    description: 'GPT-5.6系列旗舰，复杂推理与长任务'
  }
];

const SubscriptionManager = () => {
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isBatchModalVisible, setIsBatchModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [form] = Form.useForm();
  const [batchForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [rssForm] = Form.useForm();
  const [rssEditForm] = Form.useForm();
  const [manualSendForm] = Form.useForm();
  const [isRssModalVisible, setIsRssModalVisible] = useState(false);
  const [isRssEditModalVisible, setIsRssEditModalVisible] = useState(false);
  const [isManualSendModalVisible, setIsManualSendModalVisible] = useState(false);
  const [rssEditRecord, setRssEditRecord] = useState(null);
  const [currentAIModel, setCurrentAIModel] = useState(null);
  const [isModelLoading, setIsModelLoading] = useState(true);
  const queryClient = useQueryClient();
  
  // 获取当前全局 AI 模型配置
  useEffect(() => {
    const fetchCurrentModel = async () => {
      try {
        const response = await axios.get('/api/config/global-ai-model');
        // model 可能是 null（未配置）或具体模型ID
        setCurrentAIModel(response.data?.model || null);
      } catch (error) {
        console.error('Failed to fetch current AI model:', error);
        setCurrentAIModel(null);
      } finally {
        setIsModelLoading(false);
      }
    };
    fetchCurrentModel();
  }, []);
  
  // 更新全局 AI 模型配置
  const handleModelChange = async (modelId) => {
    setIsModelLoading(true);
    try {
      await axios.post('/api/config/global-ai-model', { model: modelId });
      setCurrentAIModel(modelId);
      message.success(`AI 模型已切换为 ${AI_MODELS.find(m => m.id === modelId)?.name || modelId}`);
    } catch (error) {
      message.error(error.response?.data?.error || '切换模型失败');
    } finally {
      setIsModelLoading(false);
    }
  };

  // 获取邮件订阅列表
  const { data: subscriptions = [], isLoading } = useQuery(
    'subscriptions',
    () => axios.get('/api/subscriptions').then(res => res.data)
  );

  // 获取投资组合列表
  const { data: portfolios = [], isLoading: portfoliosLoading } = useQuery(
    'portfolios',
    () => axios.get('/api/portfolios').then(res => res.data)
  );

  // 获取RSS源列表（用于管理界面）
  const { data: rssSourcesAll = [], isLoading: rssLoadingAll } = useQuery(
    'rss-sources-all',
    () => axios.get('/api/rss-sources', { params: { include_inactive: true } }).then(res => res.data)
  );

  // 获取邮件发送统计
  const { data: emailStats } = useQuery(
    'email-stats',
    () => axios.get('/api/subscriptions/email/stats').then(res => res.data)
  );

  // 获取邮件发送日志
  const { data: emailLogs = [], isLoading: logsLoading } = useQuery(
    'email-logs',
    () => axios.get('/api/subscriptions/email/logs', { params: { limit: 50 } }).then(res => res.data.logs)
  );

  // ========== 正式日报配置 ==========
  const [formalForm] = Form.useForm();
  const [formalRecipientForm] = Form.useForm();
  const [isFormalRecipientModalVisible, setIsFormalRecipientModalVisible] = useState(false);
  const [isFormalBatchModalVisible, setIsFormalBatchModalVisible] = useState(false);
  const [formalBatchForm] = Form.useForm();

  // 获取正式日报配置
  const { data: formalConfig, isLoading: formalConfigLoading } = useQuery(
    'formal-report-config',
    () => axios.get('/api/formal-report/config').then(res => res.data)
  );

  // 获取正式日报接收人
  const { data: formalRecipients = [], isLoading: formalRecipientsLoading, refetch: refetchFormalRecipients } = useQuery(
    'formal-report-recipients',
    () => axios.get('/api/formal-report/recipients').then(res => res.data)
  );

  // 更新正式日报配置
  const updateFormalConfigMutation = useMutation(
    (data) => axios.put('/api/formal-report/config', data),
    {
      onSuccess: () => {
        message.success('正式日报配置已更新');
        queryClient.invalidateQueries('formal-report-config');
      },
      onError: (error) => {
        message.error(error.response?.data?.error || '更新配置失败');
      }
    }
  );

  // 添加正式日报接收人
  const addFormalRecipientMutation = useMutation(
    (data) => axios.post('/api/formal-report/recipients', data),
    {
      onSuccess: () => {
        message.success('接收人已添加');
        queryClient.invalidateQueries('formal-report-recipients');
        setIsFormalRecipientModalVisible(false);
        formalRecipientForm.resetFields();
      },
      onError: (error) => {
        message.error(error.response?.data?.error || '添加接收人失败');
      }
    }
  );

  // 批量添加正式日报接收人
  const batchAddFormalRecipientsMutation = useMutation(
    (data) => axios.post('/api/formal-report/recipients/batch', data),
    {
      onSuccess: (response) => {
        message.success(`成功添加 ${response.data.added} 人，跳过 ${response.data.skipped} 人`);
        queryClient.invalidateQueries('formal-report-recipients');
        setIsFormalBatchModalVisible(false);
        formalBatchForm.resetFields();
      },
      onError: (error) => {
        message.error(error.response?.data?.error || '批量添加失败');
      }
    }
  );

  // 删除正式日报接收人
  const deleteFormalRecipientMutation = useMutation(
    (id) => {
      if (!id) {
        return Promise.reject(new Error('无效的接收人ID，请刷新页面后重试'));
      }
      return axios.delete(`/api/formal-report/recipients/${id}`);
    },
    {
      onSuccess: () => {
        message.success('接收人已删除');
        queryClient.invalidateQueries('formal-report-recipients');
      },
      onError: (error) => {
        message.error(error.response?.data?.error || error.message || '删除失败');
      }
    }
  );

  // 切换正式日报接收人状态
  const toggleFormalRecipientMutation = useMutation(
    (id) => axios.patch(`/api/formal-report/recipients/${id}/toggle`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('formal-report-recipients');
      },
      onError: (error) => {
        message.error(error.response?.data?.error || '切换状态失败');
      }
    }
  );

  // 批量切换所有正式日报接收人状态
  const toggleAllFormalRecipientsMutation = useMutation(
    (is_active) => axios.patch('/api/formal-report/recipients/toggle-all', { is_active }),
    {
      onSuccess: (response) => {
        const status = response.data.is_active ? '启用' : '禁用';
        message.success(`已${status}所有接收人 (${response.data.updated_count} 人)`);
        queryClient.invalidateQueries('formal-report-recipients');
      },
      onError: (error) => {
        message.error(error.response?.data?.error || '批量切换状态失败');
      }
    }
  );

  // 手动发送正式日报
  const sendFormalReportMutation = useMutation(
    () => axios.post('/api/formal-report/send'),
    {
      onSuccess: (response) => {
        message.success(`正式日报发送已启动，将发送给 ${response.data.recipientCount} 位接收人`);
      },
      onError: (error) => {
        message.error(error.response?.data?.error || '发送失败');
      }
    }
  );

  // 处理正式日报配置表单提交
  const handleFormalConfigSubmit = () => {
    formalForm.validateFields().then(values => {
      updateFormalConfigMutation.mutate(values);
    });
  };

  // 处理批量添加正式日报接收人
  const handleFormalBatchAdd = () => {
    formalBatchForm.validateFields().then(values => {
      const emails = values.emails.split(/[\n,;]+/).map(e => e.trim()).filter(e => e);
      const recipients = emails.map(email => ({ email, name: null }));
      batchAddFormalRecipientsMutation.mutate({ recipients });
    });
  };

  // 计算所有接收人是否全部启用
  const allFormalRecipientsActive = formalRecipients.length > 0 && formalRecipients.every(r => r.is_active === 1);
  const anyFormalRecipientsActive = formalRecipients.some(r => r.is_active === 1);

  // 正式日报接收人表格列
  const formalRecipientColumns = [
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: '35%'
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: '20%',
      render: (name) => name || <Text type="secondary">未设置</Text>
    },
    {
      title: () => (
        <Space>
          <span>状态</span>
          <Button
            type={allFormalRecipientsActive ? 'default' : 'primary'}
            size="small"
            loading={toggleAllFormalRecipientsMutation.isLoading}
            onClick={() => toggleAllFormalRecipientsMutation.mutate(!anyFormalRecipientsActive)}
            disabled={formalRecipients.length === 0}
          >
            {anyFormalRecipientsActive ? '全部禁用' : '全部启用'}
          </Button>
        </Space>
      ),
      dataIndex: 'is_active',
      key: 'is_active',
      width: '18%',
      render: (isActive, record) => (
        <Switch
          checked={isActive === 1}
          onChange={() => toggleFormalRecipientMutation.mutate(record.id)}
          checkedChildren="启用"
          unCheckedChildren="禁用"
        />
      )
    },
    {
      title: '添加时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '20%',
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '操作',
      key: 'actions',
      width: '10%',
      render: (_, record) => (
        <Popconfirm
          title="确定要删除这个接收人吗？"
          onConfirm={() => deleteFormalRecipientMutation.mutate(record.id)}
          okText="确定"
          cancelText="取消"
        >
          <Button type="link" danger icon={<DeleteOutlined />} size="small">
            删除
          </Button>
        </Popconfirm>
      )
    }
  ];

  // RSS源变更
  const createRssMutation = useMutation(
    (payload) => axios.post('/api/rss-sources', payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('rss-sources-active');
        queryClient.invalidateQueries('rss-sources-all');
        message.success('RSS源已创建');
        setIsRssModalVisible(false);
        rssForm.resetFields();
      },
      onError: (e) => message.error(e.response?.data?.error || '创建失败')
    }
  );
  const updateRssMutation = useMutation(
    ({ id, data }) => axios.put(`/api/rss-sources/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('rss-sources-active');
        queryClient.invalidateQueries('rss-sources-all');
        message.success('RSS源已更新');
        setIsRssEditModalVisible(false);
        rssEditForm.resetFields();
      },
      onError: (e) => message.error(e.response?.data?.error || '更新失败')
    }
  );
  const deleteRssMutation = useMutation(
    (id) => axios.delete(`/api/rss-sources/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('rss-sources-active');
        queryClient.invalidateQueries('rss-sources-all');
        message.success('RSS源已删除');
      },
      onError: (e) => message.error(e.response?.data?.error || '删除失败')
    }
  );

  // 一键导入CSV
  const importCsvMutation = useMutation(
    () => axios.post('/api/rss-sources/import-csv', {
      skipDuplicateCheck: true,
      generateSummary: true,
      parallel: 10
    }),
    {
      onSuccess: (response) => {
        const { filesCount, files } = response.data;
        message.success({
          content: `已开始导入 ${filesCount} 个CSV文件，请查看服务器日志`,
          duration: 5
        });
        console.log('导入的文件:', files);
      },
      onError: (e) => {
        message.error(e.response?.data?.error || '导入失败');
      }
    }
  );

  // 添加订阅
  const addSubscriptionMutation = useMutation(
    (data) => axios.post('/api/subscriptions', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('subscriptions');
        // 不在这里显示成功消息，在handleModalOk中统一显示
      },
      onError: (error) => {
        if (error.response?.data?.error === 'Already subscribed') {
          message.warning('该邮箱已经订阅过该投资组合');
        } else {
          message.error(error.response?.data?.error || '添加失败');
        }
      }
    }
  );

  // 批量添加订阅
  const batchAddMutation = useMutation(
    (data) => axios.post('/api/subscriptions/batch', data),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('subscriptions');
        const { added, errors } = response.data;
        message.success(`成功添加 ${added.length} 个订阅`);
        if (errors.length > 0) {
          message.warning(`${errors.length} 个邮箱添加失败`);
        }
        setIsBatchModalVisible(false);
        batchForm.resetFields();
      },
      onError: (error) => {
        message.error(error.response?.data?.error || '批量添加失败');
      }
    }
  );

  // 删除订阅
  const deleteSubscriptionMutation = useMutation(
    ({ email, portfolioId }) => axios.post('/api/subscriptions/unsubscribe', {
      email, 
      portfolio_id: portfolioId
    }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('subscriptions');
        message.success('订阅删除成功');
      },
      onError: (error) => {
        message.error(error.response?.data?.error || '删除失败');
      }
    }
  );

  // 发送手动日报 (原测试邮件)
  const manualSendMutation = useMutation(
    (data) => axios.post('/api/subscriptions/email/send-daily', data),
    {
      onSuccess: () => {
        message.success('日报发送任务已启动，请查看邮件日志');
        queryClient.invalidateQueries('email-logs');
        setIsManualSendModalVisible(false);
        manualSendForm.resetFields();
      },
      onError: (error) => {
        message.error(error.response?.data?.error || '发送失败');
      }
    }
  );

  const updateConfigMutation = useMutation(
    (payload) => axios.put('/api/subscriptions/update-config', payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('subscriptions');
        message.success('订阅配置已更新');
        setIsEditModalVisible(false);
        editForm.resetFields();
      },
      onError: (error) => {
        message.error(error.response?.data?.error || '更新失败');
      }
    }
  );

  const openEditModal = (record) => {
    setEditRecord(record);
    
    // 预填 - 解析数据库中的JSON字段
    // 注意：sources为null或空字符串时表示"包含所有新闻源"，会被解析为空数组[]
    // record.sources 可能已经是数组（从 mergedSubscriptions）或者是 JSON 字符串（直接从数据库）
    const parsedSources = (() => { 
      try { 
        if (!record.sources) return []; // null 或 undefined -> 空数组（表示全选）
        // 如果已经是数组，直接返回
        if (Array.isArray(record.sources)) return record.sources;
        // 如果是字符串，尝试解析
        if (typeof record.sources === 'string') {
          const parsed = JSON.parse(record.sources);
          return Array.isArray(parsed) ? parsed : [];
        }
        return [];
      } catch(_) { 
        return []; 
      } 
    })();
    const parsedKeywords = (() => { 
      try { 
        if (!record.keywords) return [];
        if (Array.isArray(record.keywords)) return record.keywords;
        if (typeof record.keywords === 'string') {
          const parsed = JSON.parse(record.keywords);
          return Array.isArray(parsed) ? parsed : [];
        }
        return [];
      } catch(_) { return []; } 
    })();
    
    const parsedPrompts = (() => { 
      try { 
        if (!record.prompts) return null;
        if (typeof record.prompts === 'object' && !Array.isArray(record.prompts)) return record.prompts;
        if (typeof record.prompts === 'string') {
          const parsed = JSON.parse(record.prompts);
          return typeof parsed === 'object' ? parsed : null;
        }
        return null;
      } catch(_) { return null; } 
    })();
    
    console.log('📝 编辑订阅配置:', { 
      email: record.email, 
      rawSources: record.sources,
      parsedSources: parsedSources,
      sourcesLength: parsedSources.length 
    });
    
    // 先设置表单值，然后打开对话框
    editForm.setFieldsValue({
      email: record.email,
      portfolio_id: record.portfolio_id ?? null,
      sources: parsedSources,
      send_time: record.send_time || undefined,
      use_perplexity: record.use_perplexity !== 0, // 默认true，只有明确为0才是false
      keywords_enabled: !!record.keywords_enabled,
      keywords: parsedKeywords,
      enableMacroOverviewPrompt: !!parsedPrompts?.macroOverview,
      enableHighlightsPrompt: !!parsedPrompts?.highlights,
      enablePerStockPrompt: !!parsedPrompts?.perStock,
      enableKeywordsPrompt: !!parsedPrompts?.keywords,
      macroOverviewPrompt: parsedPrompts?.macroOverview || '',
      highlightsPrompt: parsedPrompts?.highlights || '',
      perStockPrompt: parsedPrompts?.perStock || '',
      keywordsPrompt: parsedPrompts?.keywords || ''
    });
    
    // 使用 setTimeout 确保在下一个事件循环中打开对话框，让表单值先设置好
    setTimeout(() => {
      setIsEditModalVisible(true);
      // 再次确认表单值已正确设置
      console.log('✅ 表单当前 sources 值:', editForm.getFieldValue('sources'));
    }, 0);
  };

  const handleAddSubscription = () => {
    setIsModalVisible(true);
    form.resetFields();
    form.setFieldsValue({ keywords_enabled: false, keywords: [] });
  };

  const handleModalOk = () => {
    form.validateFields().then(async (values) => {
      const { email, portfolio_ids, sources, keywords_enabled, keywords, send_time, use_perplexity,
        enableMacroOverviewPrompt, enableHighlightsPrompt, enablePerStockPrompt, enableKeywordsPrompt,
        macroOverviewPrompt, highlightsPrompt, perStockPrompt, keywordsPrompt } = values;
      
      const raw = (text) => (text || '').trim();
      const prompts = {};
      const pMacro = raw(macroOverviewPrompt);
      const pHigh = raw(highlightsPrompt);
      const pStock = raw(perStockPrompt);
      const pKey = raw(keywordsPrompt);
      if (enableMacroOverviewPrompt && pMacro) prompts.macroOverview = pMacro;
      if (enableHighlightsPrompt && pHigh) prompts.highlights = pHigh;
      if (enablePerStockPrompt && pStock) prompts.perStock = pStock;
      if (enableKeywordsPrompt && pKey) prompts.keywords = pKey;
      const promptsOrNull = Object.keys(prompts).length > 0 ? prompts : null;

      try {
        if (!portfolio_ids || portfolio_ids.length === 0) {
          await addSubscriptionMutation.mutateAsync({ email, sources, keywords_enabled: !!keywords_enabled, keywords, send_time, use_perplexity: use_perplexity !== false, prompts: promptsOrNull });
          message.success('综合日报订阅添加成功');
        } else {
          const promises = portfolio_ids.map(portfolioId => 
            addSubscriptionMutation.mutateAsync({
              email,
              portfolio_id: portfolioId,
              sources,
              keywords_enabled: !!keywords_enabled,
              keywords,
              send_time,
              use_perplexity: use_perplexity !== false,
              prompts: promptsOrNull
            })
          );
          await Promise.all(promises);
          message.success(`成功添加 ${portfolio_ids.length} 个投资组合订阅`);
        }
        
        setIsModalVisible(false);
        form.resetFields();
      } catch (error) {
        // 错误已在mutation中处理
      }
    });
  };

  const handleBatchModalOk = () => {
    batchForm.validateFields().then((values) => {
      const emails = values.emails.split('\n').map(email => email.trim()).filter(email => email);
      const { portfolioIds, batchSources, batchKeywordsEnabled, batchKeywords, batchSendTime, batchUsePerplexity } = values;
      
      // 如果没有选择投资组合，则订阅综合日报
      if (!portfolioIds || portfolioIds.length === 0) {
        batchAddMutation.mutate({
          emails,
          sources: batchSources,
          keywords_enabled: !!batchKeywordsEnabled,
          keywords: batchKeywords,
          send_time: batchSendTime,
          use_perplexity: batchUsePerplexity !== false
        });
      } else {
        // 为每个选中的投资组合创建订阅
        portfolioIds.forEach(portfolioId => {
          batchAddMutation.mutate({
            emails,
            portfolio_id: portfolioId,
            sources: batchSources,
            keywords_enabled: !!batchKeywordsEnabled,
            keywords: batchKeywords,
            send_time: batchSendTime,
            use_perplexity: batchUsePerplexity !== false
          });
        });
      }
    });
  };

  const handleDeleteSubscription = (params) => {
    deleteSubscriptionMutation.mutate({
      email: params.email,
      portfolioId: params.portfolioId
    });
  };

  const handleDeleteAllSubscriptions = (email) => {
    // 找到该邮箱的所有订阅并删除
    const emailSubscriptions = subscriptions.filter(sub => sub.email === email);
    emailSubscriptions.forEach(subscription => {
      deleteSubscriptionMutation.mutate({
        email: subscription.email,
        portfolioId: subscription.portfolio_id
      });
    });
  };

  const handleEditOk = () => {
    editForm.validateFields().then(values => {
      const payload = {
        email: values.email,
        portfolio_id: values.portfolio_id,
        sources: values.sources,
        send_time: values.send_time,
        use_perplexity: values.use_perplexity !== false,
        keywords_enabled: !!values.keywords_enabled,
        keywords: values.keywords
      };

      const raw = (text) => (text || '').trim();
      const prompts = {};
      const pMacro = raw(values.macroOverviewPrompt);
      const pHigh = raw(values.highlightsPrompt);
      const pStock = raw(values.perStockPrompt);
      const pKey = raw(values.keywordsPrompt);
      if (values.enableMacroOverviewPrompt && pMacro) prompts.macroOverview = pMacro;
      if (values.enableHighlightsPrompt && pHigh) prompts.highlights = pHigh;
      if (values.enablePerStockPrompt && pStock) prompts.perStock = pStock;
      if (values.enableKeywordsPrompt && pKey) prompts.keywords = pKey;
      payload.prompts = Object.keys(prompts).length > 0 ? prompts : null;

      updateConfigMutation.mutate(payload);
    });
  };

  const openManualSendModal = () => {
    setIsManualSendModalVisible(true);
    manualSendForm.resetFields();
  };

  const handleManualSend = () => {
    manualSendForm.validateFields().then(values => {
      // If no emails selected, send to empty array or null (which might mean "all" depending on backend logic?)
      // Wait, backend logic: if recipientEmails provided, filter. If NOT provided, send to all time-eligible.
      // But for manual send, we want to force send to selected.
      // If user selects nothing, maybe we should warn or send to all?
      // The requirement says "select subscribers... then send".
      // If empty, let's assume "send to all" or require selection.
      // Let's assume we want to send to selected. If empty, maybe ask confirmation to send to ALL?
      // For now let's just pass the list. If list is empty/undefined, backend behavior is "send to all (filtered by time/weekend)".
      // But this is "Manual Send", so we probably want "Send to ALL active subscribers immediately regardless of time".
      // Let's look at my backend change:
      // if (recipientEmails && ... length > 0) filter.
      // if not provided, it does the default "timeEligible" check.
      // We want to skip time check for manual send even if "All" is intended.
      // But the UI is a multi-select. If I select nothing, it might mean "All".
      // Let's make it required to select at least one, OR provide a "Send to All" switch.
      // The prompt says "can multi-select... option box includes all our subscribers".
      
      const emails = values.recipients;
      manualSendMutation.mutate({ emails });
    });
  };

  // 处理CSV批量导入
  const handleImportCsv = () => {
    Modal.confirm({
      title: '确认导入CSV文件',
      content: '将导入 /Users/yuligu/Library/CloudStorage/OneDrive-DECENT（HK）GROUPHOLDINGSLIMITED/news.csv 到新闻数据库。确认继续？',
      okText: '确认导入',
      cancelText: '取消',
      onOk: () => {
        importCsvMutation.mutate();
      }
    });
  };

  const openRssCreate = () => {
    setIsRssModalVisible(true);
    rssForm.resetFields();
  };
  const openRssEdit = (record) => {
    setRssEditRecord(record);
    setIsRssEditModalVisible(true);
    rssEditForm.setFieldsValue({ name: record.name, url: record.url, is_active: !!record.is_active });
  };

  const handleCreateRss = () => {
    rssForm.validateFields().then(values => {
      createRssMutation.mutate({ name: values.name, url: values.url, is_active: values.is_active });
    });
  };
  const handleUpdateRss = () => {
    rssEditForm.validateFields().then(values => {
      updateRssMutation.mutate({ id: rssEditRecord.id, data: { name: values.name, url: values.url, is_active: values.is_active } });
    });
  };

  const subscriptionColumns = [
    {
      title: '邮件地址',
      dataIndex: 'email',
      key: 'email',
      render: (email, record) => (
        <Space direction="vertical" size="small">
          <Space>
            <Avatar size="small" icon={<MailOutlined />} />
            {email}
            {record.subscriptionCount > 1 && (
              <Tag color="blue">{record.subscriptionCount}个订阅</Tag>
            )}
            {record.sources && (
              <Space size="small">
                {record.sources.map(src => (
                  <Tag key={src} color="geekblue">{src}</Tag>
                ))}
              </Space>
            )}
            {record.keywords_enabled ? (
              <Tag color="purple">关键词已开</Tag>
            ) : null}
          </Space>
        </Space>
      )
    },
    {
      title: '订阅类型',
      dataIndex: 'portfolio_name',
      key: 'portfolio_name',
      render: (name, record) => (
        <Space direction="vertical" size="small">
          {record.hasGeneralSubscription && (
            <Tag color="purple">综合日报</Tag>
          )}
          {record.portfolios && record.portfolios.length > 0 && 
            record.portfolios.map(portfolio => (
              <Tag key={portfolio.id} color="blue">{portfolio.name}</Tag>
            ))
          }
        </Space>
      )
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'} icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {isActive ? '活跃' : '暂停'}
        </Tag>
      )
    },
    {
      title: '发送时间',
      dataIndex: 'send_time',
      key: 'send_time',
      render: (time) => time || <Text type="secondary">默认 (17:09)</Text>
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          {record.hasGeneralSubscription && (
            <Popconfirm
              title="确定要删除综合日报订阅吗？"
              onConfirm={() => handleDeleteSubscription({ email: record.email, portfolioId: null })}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" danger size="small">
                删除综合日报
              </Button>
            </Popconfirm>
          )}
          {record.portfolios && record.portfolios.length > 0 && 
            record.portfolios.map(portfolio => (
              <Popconfirm
                key={portfolio.id}
                title={`确定要删除"${portfolio.name}"订阅吗？`}
                onConfirm={() => handleDeleteSubscription({ email: record.email, portfolioId: portfolio.id })}
                okText="确定"
                cancelText="取消"
              >
                <Button type="link" danger size="small">
                  删除{portfolio.name}
                </Button>
              </Popconfirm>
            ))
          }
          {record.subscriptionCount > 1 && (
            <Popconfirm
              title="确定要删除该邮箱的所有订阅吗？"
              onConfirm={() => handleDeleteAllSubscriptions(record.email)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" danger size="small">
                删除全部
              </Button>
            </Popconfirm>
          )}
          <Button type="link" size="small" onClick={() => openEditModal(record)}>
            修改配置
          </Button>
        </Space>
      )
    }
  ];

  const emailLogColumns = [
    {
      title: '接收者',
      dataIndex: 'recipient',
      key: 'recipient',
      width: '25%'
    },
    {
      title: '主题',
      dataIndex: 'subject',
      key: 'subject',
      width: '35%'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: '15%',
      render: (status) => (
        <Tag color={status === 'sent' ? 'green' : status === 'failed' ? 'red' : 'orange'}>
          {status === 'sent' ? '已发送' : status === 'failed' ? '失败' : '待发送'}
        </Tag>
      )
    },
    {
      title: '发送时间',
      dataIndex: 'sent_at',
      key: 'sent_at',
      width: '15%',
      render: (date) => dayjs(date).format('MM-DD HH:mm')
    },
    {
      title: '错误信息',
      dataIndex: 'error_message',
      key: 'error_message',
      width: '10%',
      render: (error) => error ? (
        <Text type="danger" ellipsis={{ tooltip: error }}>
          查看错误
        </Text>
      ) : '-'
    }
  ];

  // 计算统计数据
  const totalSubscriptions = subscriptions.length;
  const activeSubscriptions = subscriptions.filter(sub => sub.is_active).length;

  // 合并同一邮箱的多个订阅
  const mergedSubscriptions = subscriptions.reduce((acc, subscription) => {
    const existingEmail = acc.find(item => item.email === subscription.email);
    
    const sources = (() => { try { return subscription.sources ? JSON.parse(subscription.sources) : null; } catch (_) { return null; } })();
    const keywordsEnabled = !!subscription.keywords_enabled;

    if (existingEmail) {
      // 如果已有该邮箱，添加投资组合信息
      if (subscription.portfolio_name) {
        existingEmail.portfolios = existingEmail.portfolios || [];
        existingEmail.portfolios.push({
          id: subscription.portfolio_id,
          name: subscription.portfolio_name
        });
      } else {
        existingEmail.hasGeneralSubscription = true;
      }
      if (Array.isArray(sources) && sources.length > 0) {
        existingEmail.sources = sources;
      }
      if (keywordsEnabled) existingEmail.keywords_enabled = true;
      existingEmail.subscriptionCount = (existingEmail.subscriptionCount || 1) + 1;
    } else {
      // 新邮箱
      const newSubscription = {
        ...subscription,
        sources: Array.isArray(sources) && sources.length > 0 ? sources : null,
        keywords_enabled: keywordsEnabled,
        subscriptionCount: 1,
        portfolios: subscription.portfolio_name ? [{
          id: subscription.portfolio_id,
          name: subscription.portfolio_name
        }] : [],
        hasGeneralSubscription: !subscription.portfolio_name
      };
      acc.push(newSubscription);
    }
    
    return acc;
  }, []);

  return (
    <div>
      <div className="page-header">
        <Title level={2} className="page-title">订阅管理</Title>
        <Text className="page-description">
          管理综合日报邮件订阅用户，监控邮件发送状态，支持批量操作
        </Text>
      </div>

      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        items={[
          {
            key: 'subscriptions',
            label: '订阅管理',
            children: (
              <>
                <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                  <Col xs={24} sm={12} md={8}>
                    <Card>
                      <Statistic
                        title="总邮箱数"
                        value={mergedSubscriptions.length}
                        prefix={<TeamOutlined />}
                        valueStyle={{ color: '#1890ff' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Card>
                      <Statistic
                        title="活跃订阅"
                        value={activeSubscriptions}
                        prefix={<CheckCircleOutlined />}
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Card>
                      <Statistic
                        title="总订阅数"
                        value={totalSubscriptions}
                        prefix={<MailOutlined />}
                        valueStyle={{ color: '#fa8c16' }}
                      />
                    </Card>
                  </Col>
                </Row>

                <Card
                  title="邮件订阅列表"
                  extra={
                    <Space wrap>
                      <Button
                        icon={<SendOutlined />}
                        onClick={openManualSendModal}
                        loading={manualSendMutation.isLoading}
                      >
                        手动发送日报
                      </Button>
                      <Button
                        icon={<UserAddOutlined />}
                        onClick={() => setIsBatchModalVisible(true)}
                      >
                        批量添加
                      </Button>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddSubscription}
                      >
                        添加订阅
                      </Button>
                    </Space>
                  }
                >
                  <Table
                    columns={subscriptionColumns}
                    dataSource={mergedSubscriptions}
                    rowKey="email"
                    loading={isLoading}
                    scroll={{ x: 1200 }}
                    pagination={{
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
                    }}
                  />
                </Card>
              </>
            )
          },
          {
            key: 'logs',
            label: '邮件日志',
            children: (
              <Card
                title="邮件发送日志"
                extra={
                  <Space wrap>
                  </Space>
                }
              >
                {emailStats && (
                  <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    <Col xs={24} sm={12} md={6}>
                      <Card size="small">
                        <Statistic
                          title="今日发送"
                          value={emailStats.todaySent || 0}
                          valueStyle={{ color: '#52c41a' }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card size="small">
                        <Statistic
                          title="今日失败"
                          value={emailStats.todayFailed || 0}
                          valueStyle={{ color: '#ff4d4f' }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card size="small">
                        <Statistic
                          title="本周发送"
                          value={emailStats.weekSent || 0}
                          valueStyle={{ color: '#1890ff' }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Card size="small">
                        <Statistic
                          title="成功率"
                          value={emailStats.successRate || 0}
                          suffix="%"
                          valueStyle={{ color: '#722ed1' }}
                        />
                      </Card>
                    </Col>
                  </Row>
                )}

                <Table
                  columns={emailLogColumns}
                  dataSource={emailLogs}
                  rowKey="id"
                  loading={logsLoading}
                  pagination={{
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
                  }}
                  scroll={{ x: 1000 }}
                />
              </Card>
            )
          },
          {
            key: 'ai-model',
            label: (
              <span>
                <RobotOutlined style={{ marginRight: 8 }} />
                AI 模型设置
              </span>
            ),
            children: (
              <Card
                title={
                  <Space>
                    <SettingOutlined />
                    <span>全局 AI 模型配置</span>
                  </Space>
                }
              >
                <Alert
                  message="关于 AI 模型"
                  description="此设置会影响所有日报的 AI 生成内容。选择不同的模型将改变新闻分析、摘要生成的风格和质量。更换模型后，Notion 页面标题会显示当前使用的模型名称。"
                  type="info"
                  showIcon
                  style={{ marginBottom: 24 }}
                />
                
                <div style={{ marginBottom: 24 }}>
                  <Title level={5} style={{ marginBottom: 16 }}>
                    当前使用的模型
                  </Title>
                  {currentAIModel ? (
                    <>
                      <Tag color="blue" style={{ fontSize: 16, padding: '8px 16px' }}>
                        {AI_MODELS.find(m => m.id === currentAIModel)?.name || currentAIModel}
                      </Tag>
                      <Text type="secondary" style={{ marginLeft: 12 }}>
                        ({AI_MODELS.find(m => m.id === currentAIModel)?.provider || 'Unknown'})
                      </Text>
                    </>
                  ) : (
                    <Tag color="warning" style={{ fontSize: 16, padding: '8px 16px' }}>
                      ⚠️ 未配置 - 请选择一个模型
                    </Tag>
                  )}
                </div>

                <Divider />

                <Title level={5} style={{ marginBottom: 16 }}>
                  选择 AI 模型
                </Title>
                
                <Radio.Group 
                  value={currentAIModel} 
                  onChange={(e) => handleModelChange(e.target.value)}
                  style={{ width: '100%' }}
                  disabled={isModelLoading}
                >
                  <Row gutter={[16, 16]}>
                    {AI_MODELS.map(model => (
                      <Col xs={24} sm={12} key={model.id}>
                        <Card
                          hoverable
                          style={{
                            borderColor: currentAIModel === model.id ? '#1890ff' : undefined,
                            backgroundColor: currentAIModel === model.id ? '#e6f7ff' : undefined
                          }}
                          onClick={() => !isModelLoading && handleModelChange(model.id)}
                        >
                          <Radio value={model.id} style={{ width: '100%' }}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <Space>
                                <Text strong style={{ fontSize: 16 }}>{model.name}</Text>
                                <Tag color={
                                  model.provider === 'OpenAI' ? 'green' : 
                                  model.provider === 'Google' ? 'blue' : 
                                  model.provider === 'Anthropic' ? 'purple' : 'default'
                                }>
                                  {model.provider}
                                </Tag>
                              </Space>
                              <Text type="secondary" style={{ marginTop: 8 }}>
                                {model.description}
                              </Text>
                              <Text code style={{ marginTop: 8, fontSize: 12 }}>
                                {model.id}
                              </Text>
                            </div>
                          </Radio>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Radio.Group>

                <Divider />

                <div style={{ 
                  padding: 16, 
                  background: '#fafafa', 
                  borderRadius: 8,
                  marginTop: 16 
                }}>
                  <Title level={5} style={{ marginBottom: 8 }}>
                    💡 模型选择建议
                  </Title>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    <li><Text><strong>GPT-5.2 Pro</strong> - 推荐用于日常使用，平衡速度和质量</Text></li>
                    <li><Text><strong>Claude Opus 4.5</strong> - 擅长长文本理解和多轮推理</Text></li>
                    <li><Text><strong>Gemini 3 Pro</strong> - Google 高级模型，综合能力强</Text></li>
                  </ul>
                </div>
              </Card>
            )
          },
          {
            key: 'formal-report',
            label: (
              <span>
                <MailOutlined style={{ marginRight: 8 }} />
                正式日报
              </span>
            ),
            children: (
              <div>
                {/* 配置卡片 */}
                <Card
                  title={
                    <Space>
                      <SettingOutlined />
                      <span>正式日报配置</span>
                    </Space>
                  }
                  style={{ marginBottom: 16 }}
                  loading={formalConfigLoading}
                >
                  <Alert
                    message="关于正式日报"
                    description="正式日报会读取指定的 Notion 页面内容，格式化后发送给所有活跃的接收人。发送时间统一设置，到点后所有接收人同时收到邮件。"
                    type="info"
                    showIcon
                    style={{ marginBottom: 24 }}
                  />

                  <Form
                    form={formalForm}
                    layout="vertical"
                    initialValues={formalConfig}
                    onFinish={handleFormalConfigSubmit}
                    key={formalConfig?.send_time}
                  >
                    <Row gutter={24}>
                      <Col xs={24} sm={12} md={8}>
                        <Form.Item
                          name="send_time"
                          label="发送时间"
                          rules={[
                            { required: true, message: '请设置发送时间' },
                            { pattern: /^\d{2}:\d{2}$/, message: '格式: HH:mm' }
                          ]}
                          initialValue={formalConfig?.send_time || '10:30'}
                        >
                          <Input placeholder="10:30" maxLength={5} style={{ width: 120 }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Form.Item
                          name="notion_page_id"
                          label="Notion 页面 ID"
                          initialValue={formalConfig?.notion_page_id || '2beecf61950880c8b356efc65a263a50'}
                        >
                          <Input placeholder="Notion Page ID" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Form.Item
                          name="is_enabled"
                          label="启用状态"
                          valuePropName="checked"
                          initialValue={formalConfig?.is_enabled === 1}
                        >
                          <Switch checkedChildren="启用" unCheckedChildren="禁用" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Space>
                      <Button 
                        type="primary" 
                        htmlType="submit"
                        loading={updateFormalConfigMutation.isLoading}
                      >
                        保存配置
                      </Button>
                      <Button 
                        icon={<SendOutlined />}
                        onClick={() => sendFormalReportMutation.mutate()}
                        loading={sendFormalReportMutation.isLoading}
                      >
                        立即发送正式日报
                      </Button>
                    </Space>
                  </Form>
                </Card>

                {/* 接收人管理卡片 */}
                <Card
                  title={
                    <Space>
                      <TeamOutlined />
                      <span>接收人列表</span>
                      <Tag color="blue">{formalRecipients.length} 人</Tag>
                      <Tag color="green">{formalRecipients.filter(r => r.is_active === 1).length} 活跃</Tag>
                    </Space>
                  }
                  extra={
                    <Space>
                      <Button
                        icon={<UserAddOutlined />}
                        onClick={() => setIsFormalBatchModalVisible(true)}
                      >
                        批量添加
                      </Button>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setIsFormalRecipientModalVisible(true)}
                      >
                        添加接收人
                      </Button>
                    </Space>
                  }
                >
                  <Table
                    columns={formalRecipientColumns}
                    dataSource={formalRecipients}
                    rowKey="id"
                    loading={formalRecipientsLoading}
                    pagination={{
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
                    }}
                  />
                </Card>
              </div>
            )
          },
          {
            key: 'report-sections',
            label: (
              <span>
                <SettingOutlined style={{ marginRight: 8 }} />
                日报板块
              </span>
            ),
            children: <ReportSectionManager />
          }
        ]}
      />

      {/* 添加订阅弹窗 */}
      <Modal
        title="添加邮件订阅"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        confirmLoading={addSubscriptionMutation.isLoading}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="email"
            label="邮件地址"
            rules={[
              { required: true, message: '请输入邮件地址' },
              { type: 'email', message: '请输入有效的邮件地址' }
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>
          
          <Form.Item
            name="portfolio_ids"
            label="订阅投资组合"
            help="可选择多个投资组合，不选择则订阅综合日报"
          >
            <Select 
              mode="multiple"
              placeholder="选择投资组合（可选）"
              allowClear
              loading={portfoliosLoading}
            >
              {portfolios.map(portfolio => (
                <Option key={portfolio.id} value={portfolio.id}>
                  {portfolio.name}
                  {portfolio.description && (
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      - {portfolio.description}
                    </Text>
                  )}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item shouldUpdate noStyle>
            {() => {
              const selectedSources = form.getFieldValue('sources') || [];
              const isAllSources = selectedSources.length === 0;
              return (
                <Form.Item
                  name="sources"
                  label={
                    <Space>
                      <span>新闻来源（可选）</span>
                      {isAllSources ? (
                        <Tag color="green">将包含所有新闻源</Tag>
                      ) : (
                        <Tag color="blue">已选 {selectedSources.length} 个</Tag>
                      )}
                    </Space>
                  }
                  help="勾选需要的新闻源；不勾选任何项则包含所有新闻源"
                >
                  <Checkbox.Group style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {NEWS_SOURCES.map(src => (
                      <Checkbox key={src.name} value={src.name}>{src.label}</Checkbox>
                    ))}
                  </Checkbox.Group>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            name="send_time"
            label="发送时间（本地时区，HH:mm）"
            tooltip="留空表示不限时间；设置后仅在该分钟触发发送"
            rules={[{ pattern: /^\d{2}:\d{2}$/, message: '格式应为HH:mm，例如 09:30' }]}
          >
            <Input placeholder="例如 09:30" maxLength={5} />
          </Form.Item>

          <Form.Item name="use_perplexity" label="AI引擎选择" valuePropName="checked" initialValue={true} tooltip="Perplexity：实时搜索，更准确；GPT：基于数据库，更快速">
            <Switch checkedChildren="Perplexity" unCheckedChildren="GPT" />
          </Form.Item>

          <Form.Item name="keywords_enabled" label="开启关键词搜索" valuePropName="checked" initialValue={false}>
            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() => form.getFieldValue('keywords_enabled') ? (
              <Form.Item
                name="keywords"
                label="关键词（多个）"
                help="输入关键词后按回车添加；示例：Nvidia, chip, antitrust"
                rules={[{ validator:(_,v)=> Promise.resolve() }]}
              >
                <Select mode="tags" placeholder="输入并回车添加关键词" tokenSeparators={[',']}></Select>
              </Form.Item>
            ) : null}
          </Form.Item>

          <Typography.Title level={5} style={{ marginTop: 12 }}>自定义提示词（可选）</Typography.Title>
          <Paragraph type="secondary" style={{ marginTop: -8 }}>为不同板块设置你的偏好指令，关闭则使用默认提示词。</Paragraph>

          <Form.Item name="enableMacroOverviewPrompt" label="开启宏观概览提示词" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() => form.getFieldValue('enableMacroOverviewPrompt') ? (
              <Form.Item name="macroOverviewPrompt" label="宏观概览提示词">
                <TextArea rows={3} placeholder="例如：更关注利率决策与通胀数据，避免主观判断。" />
              </Form.Item>
            ) : null}
          </Form.Item>

          <Form.Item name="enableHighlightsPrompt" label="开启市场行情精选提示词" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() => form.getFieldValue('enableHighlightsPrompt') ? (
              <Form.Item name="highlightsPrompt" label="市场行情精选提示词">
                <TextArea rows={3} placeholder="例如：优先挑选与大型科技股波动直接相关的新闻。" />
              </Form.Item>
            ) : null}
          </Form.Item>

          <Form.Item name="enablePerStockPrompt" label="开启每股一条提示词" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() => form.getFieldValue('enablePerStockPrompt') ? (
              <Form.Item name="perStockPrompt" label="每股一条提示词">
                <TextArea rows={3} placeholder="例如：更关注基本面变化和监管事件，避免仅价格波动。" />
              </Form.Item>
            ) : null}
          </Form.Item>

          <Form.Item name="enableKeywordsPrompt" label="开启关键词匹配提示词" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() => form.getFieldValue('enableKeywordsPrompt') ? (
              <Form.Item name="keywordsPrompt" label="关键词匹配提示词">
                <TextArea rows={3} placeholder="例如：将军工、核能与自动驾驶政策作为优先关键词主题。" />
              </Form.Item>
            ) : null}
          </Form.Item>
        </Form>
      </Modal>

      {/* RSS 创建弹窗 */}
      <Modal
        title="新增新闻源"
        open={isRssModalVisible}
        onOk={handleCreateRss}
        onCancel={()=>{ setIsRssModalVisible(false); rssForm.resetFields(); }}
        confirmLoading={createRssMutation.isLoading}
      >
        <Form form={rssForm} layout="vertical">
          <div style={{ marginBottom: 16, padding: 12, background: '#fffbe6', borderRadius: 4, border: '1px solid #ffe58f' }}>
            <Text type="secondary">
              💡 <strong>提示：</strong>新闻源名称用于Excel导入时的source字段，以及订阅时的来源筛选。URL可选填。
            </Text>
          </div>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]} tooltip="此名称应与Excel中的source字段一致">
            <Input placeholder="如：Bloomberg, WSJ Business, Financial Times" />
          </Form.Item>
          <Form.Item name="url" label="参考URL（可选）" tooltip="仅用于记录，不影响功能">
            <Input placeholder="https://www.bloomberg.com" />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked" initialValue={true} tooltip="禁用后，此来源将不出现在订阅筛选中">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* RSS 编辑弹窗 */}
      <Modal
        title="编辑新闻源"
        open={isRssEditModalVisible}
        onOk={handleUpdateRss}
        onCancel={()=>{ setIsRssEditModalVisible(false); rssEditForm.resetFields(); }}
        confirmLoading={updateRssMutation.isLoading}
      >
        <Form form={rssEditForm} layout="vertical">
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]} tooltip="此名称应与Excel中的source字段一致">
            <Input />
          </Form.Item>
          <Form.Item name="url" label="参考URL（可选）" tooltip="仅用于记录，不影响功能">
            <Input />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked" tooltip="禁用后，此来源将不出现在订阅筛选中">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量添加弹窗 */}
      <Modal
        title="批量添加邮件订阅"
        open={isBatchModalVisible}
        onOk={handleBatchModalOk}
        onCancel={() => {
          setIsBatchModalVisible(false);
          batchForm.resetFields();
        }}
        confirmLoading={batchAddMutation.isLoading}
        width={600}
      >
        <Form
          form={batchForm}
          layout="vertical"
        >
          <Form.Item
            name="emails"
            label="邮件地址列表"
            rules={[{ required: true, message: '请输入邮件地址' }]}
            help="每行输入一个邮件地址"
          >
            <TextArea
              placeholder={`user1@example.com
user2@example.com
user3@example.com`}
              rows={6}
            />
          </Form.Item>
          
          <Form.Item
            name="portfolioIds"
            label="订阅投资组合"
            help="可选择多个投资组合，不选择则订阅综合日报"
          >
            <Select 
              mode="multiple"
              placeholder="选择投资组合（可选）"
              allowClear
              loading={portfoliosLoading}
            >
              {portfolios.map(portfolio => (
                <Option key={portfolio.id} value={portfolio.id}>
                  {portfolio.name}
                  {portfolio.description && (
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      - {portfolio.description}
                    </Text>
                  )}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item shouldUpdate noStyle>
            {() => {
              const selectedSources = batchForm.getFieldValue('batchSources') || [];
              const isAllSources = selectedSources.length === 0;
              return (
                <Form.Item
                  name="batchSources"
                  label={
                    <Space>
                      <span>新闻来源（可选）</span>
                      {isAllSources ? (
                        <Tag color="green">将包含所有新闻源</Tag>
                      ) : (
                        <Tag color="blue">已选 {selectedSources.length} 个</Tag>
                      )}
                    </Space>
                  }
                  help="勾选需要的新闻源；不勾选任何项则包含所有新闻源"
                >
                  <Checkbox.Group style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {NEWS_SOURCES.map(src => (
                      <Checkbox key={src.name} value={src.name}>{src.label}</Checkbox>
                    ))}
                  </Checkbox.Group>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            name="batchSendTime"
            label="发送时间（本地时区，HH:mm）"
            tooltip="留空表示不限时间；设置后仅在该分钟触发发送"
            rules={[{ pattern: /^\d{2}:\d{2}$/, message: '格式应为HH:mm，例如 09:30' }]}
          >
            <Input placeholder="例如 09:30" maxLength={5} />
          </Form.Item>

          <Form.Item name="batchUsePerplexity" label="AI引擎选择" valuePropName="checked" initialValue={true} tooltip="Perplexity：实时搜索，更准确；GPT：基于数据库，更快速">
            <Switch checkedChildren="Perplexity" unCheckedChildren="GPT" />
          </Form.Item>

          <Form.Item name="batchKeywordsEnabled" label="开启关键词搜索" valuePropName="checked" initialValue={false}>
            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() => batchForm.getFieldValue('batchKeywordsEnabled') ? (
              <Form.Item
                name="batchKeywords"
                label="关键词（多个）"
                help="输入关键词后按回车添加"
              >
                <Select mode="tags" placeholder="输入并回车添加关键词" tokenSeparators={[',']}></Select>
              </Form.Item>
            ) : null}
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑配置弹窗 */}
      <Modal
        title="修改订阅配置"
        open={isEditModalVisible}
        onOk={handleEditOk}
        confirmLoading={updateConfigMutation.isLoading}
        onCancel={() => {
          setIsEditModalVisible(false);
          editForm.resetFields();
        }}
        forceRender
        destroyOnClose={false}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="email" label="邮箱">
            <Input disabled />
          </Form.Item>
          <Form.Item name="portfolio_id" label="投资组合ID（留空表示综合订阅）">
            <Input disabled />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() => {
              const selectedSources = editForm.getFieldValue('sources') || [];
              const isAllSources = selectedSources.length === 0;
              return (
                <Form.Item 
                  name="sources" 
                  label={
                    <Space>
                      <span>新闻来源</span>
                      {isAllSources ? (
                        <Tag color="green">当前：包含所有新闻源</Tag>
                      ) : (
                        <Tag color="blue">当前：已选 {selectedSources.length} 个</Tag>
                      )}
                    </Space>
                  }
                  help="勾选需要的新闻源；不勾选任何项则包含所有新闻源"
                >
                  <Checkbox.Group style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {NEWS_SOURCES.map(src => (
                      <Checkbox key={src.name} value={src.name}>{src.label}</Checkbox>
                    ))}
                  </Checkbox.Group>
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item name="send_time" label="发送时间（本地时区，HH:mm）" rules={[{ pattern: /^\d{2}:\d{2}$/, message: '格式应为HH:mm，例如 09:30' }]}>
            <Input placeholder="例如 09:30" maxLength={5} />
          </Form.Item>
          <Form.Item name="use_perplexity" label="AI引擎选择" valuePropName="checked" tooltip="Perplexity：实时搜索，更准确；GPT：基于数据库，更快速">
            <Switch checkedChildren="Perplexity" unCheckedChildren="GPT" />
          </Form.Item>
          <Form.Item name="keywords_enabled" label="开启关键词搜索" valuePropName="checked" initialValue={false}>
            <Switch checkedChildren="开启" unCheckedChildren="关闭" />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() => editForm.getFieldValue('keywords_enabled') ? (
              <Form.Item name="keywords" label="关键词（多个)">
                <Select mode="tags" placeholder="输入并回车添加关键词" tokenSeparators={[',']}></Select>
              </Form.Item>
            ) : null}
          </Form.Item>

          <Typography.Title level={5} style={{ marginTop: 12 }}>自定义提示词（可选）</Typography.Title>
          <Paragraph type="secondary" style={{ marginTop: -8 }}>为不同板块设置你的偏好指令，关闭则使用默认提示词。</Paragraph>

          <Form.Item name="enableMacroOverviewPrompt" label="开启宏观概览提示词" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() => editForm.getFieldValue('enableMacroOverviewPrompt') ? (
              <Form.Item name="macroOverviewPrompt" label="宏观概览提示词">
                <TextArea rows={3} />
              </Form.Item>
            ) : null}
          </Form.Item>

          <Form.Item name="enableHighlightsPrompt" label="开启市场行情精选提示词" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() => editForm.getFieldValue('enableHighlightsPrompt') ? (
              <Form.Item name="highlightsPrompt" label="市场行情精选提示词">
                <TextArea rows={3} />
              </Form.Item>
            ) : null}
          </Form.Item>

          <Form.Item name="enablePerStockPrompt" label="开启每股一条提示词" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() => editForm.getFieldValue('enablePerStockPrompt') ? (
              <Form.Item name="perStockPrompt" label="每股一条提示词">
                <TextArea rows={3} />
              </Form.Item>
            ) : null}
          </Form.Item>

          <Form.Item name="enableKeywordsPrompt" label="开启关键词匹配提示词" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() => editForm.getFieldValue('enableKeywordsPrompt') ? (
              <Form.Item name="keywordsPrompt" label="关键词匹配提示词">
                <TextArea rows={3} />
              </Form.Item>
            ) : null}
          </Form.Item>
        </Form>
      </Modal>

      {/* 手动发送日报弹窗 */}
      <Modal
        title="手动发送日报"
        open={isManualSendModalVisible}
        onOk={handleManualSend}
        onCancel={() => {
          setIsManualSendModalVisible(false);
          manualSendForm.resetFields();
        }}
        confirmLoading={manualSendMutation.isLoading}
      >
        <Form form={manualSendForm} layout="vertical">
          <div style={{ marginBottom: 16, padding: 12, background: '#e6f7ff', borderRadius: 4, border: '1px solid #91d5ff' }}>
             <Text type="secondary">
               💡 选择要发送日报的订阅者。如果不选择任何收件人，将发送给<strong>所有</strong>订阅者。
             </Text>
          </div>
          <Form.Item 
            name="recipients" 
            label="收件人列表" 
            help="支持多选；留空则发送给所有人"
          >
            <Select 
              mode="multiple" 
              placeholder="请选择收件人" 
              allowClear
              filterOption={(input, option) => {
                const children = String(option.children || '');
                const value = String(option.value || '');
                return children.toLowerCase().indexOf(input.toLowerCase()) >= 0 || 
                       value.toLowerCase().indexOf(input.toLowerCase()) >= 0;
              }}
              style={{ width: '100%' }}
            >
              {mergedSubscriptions.map(sub => (
                <Option key={sub.email} value={sub.email}>
                  {sub.email} {sub.portfolios && sub.portfolios.length > 0 ? `(${sub.portfolios.map(p => p.name).join(', ')})` : '(综合)'}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 添加正式日报接收人弹窗 */}
      <Modal
        title="添加正式日报接收人"
        open={isFormalRecipientModalVisible}
        onOk={() => {
          formalRecipientForm.validateFields().then(values => {
            addFormalRecipientMutation.mutate(values);
          });
        }}
        onCancel={() => {
          setIsFormalRecipientModalVisible(false);
          formalRecipientForm.resetFields();
        }}
        confirmLoading={addFormalRecipientMutation.isLoading}
      >
        <Form form={formalRecipientForm} layout="vertical">
          <Form.Item
            name="email"
            label="邮箱地址"
            rules={[
              { required: true, message: '请输入邮箱地址' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>
          <Form.Item
            name="name"
            label="姓名（可选）"
          >
            <Input placeholder="接收人姓名" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量添加正式日报接收人弹窗 */}
      <Modal
        title="批量添加正式日报接收人"
        open={isFormalBatchModalVisible}
        onOk={handleFormalBatchAdd}
        onCancel={() => {
          setIsFormalBatchModalVisible(false);
          formalBatchForm.resetFields();
        }}
        confirmLoading={batchAddFormalRecipientsMutation.isLoading}
        width={600}
      >
        <Form form={formalBatchForm} layout="vertical">
          <Form.Item
            name="emails"
            label="邮箱列表"
            rules={[{ required: true, message: '请输入邮箱列表' }]}
            help="每行一个邮箱，或用逗号、分号分隔"
          >
            <TextArea
              rows={10}
              placeholder={`user1@example.com
user2@example.com
user3@example.com`}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SubscriptionManager;
